import type AccountStoreData from '@/types/AccountStoreData'
import { CareerPathType } from '@/units/ds/types/career-path-type.enum'

export const EDUCATION_TYPES = [
  CareerPathType.SCHOOL_EDUCATION,
  CareerPathType.APPRENTICESHIP,
  CareerPathType.STUDIES,
] as const

export type EducationType = (typeof EDUCATION_TYPES)[number]

export type EducationEntry = {
  type: EducationType
  uuid: string
  placeName: string
  from: string
  to: string | null
  graduation: string
  courseName: string | null
}

type SchoolEducation = AccountStoreData['schoolEducations'][number]
type Study = AccountStoreData['studies'][number]
type Apprenticeship = AccountStoreData['apprenticeships'][number]

const sliceDateForInput = (iso: string | null | undefined) => {
  if (!iso) return ''
  return iso.length >= 10 ? iso.slice(0, 10) : iso
}

const schoolEducationToEntry = (entry: SchoolEducation): EducationEntry => ({
  type: CareerPathType.SCHOOL_EDUCATION,
  uuid: entry.uuid,
  placeName: entry.schoolName ?? '',
  from: entry.from ?? '',
  to: entry.to ?? null,
  graduation: entry.graduation ?? '',
  courseName: null,
})

const apprenticeshipToEntry = (entry: Apprenticeship): EducationEntry => ({
  type: CareerPathType.APPRENTICESHIP,
  uuid: entry.uuid,
  placeName: entry.companyName ?? '',
  from: entry.from ?? '',
  to: entry.to ?? null,
  graduation: entry.graduation ?? '',
  courseName: null,
})

const studyToEntry = (entry: Study): EducationEntry => ({
  type: CareerPathType.STUDIES,
  uuid: entry.uuid,
  placeName: entry.universityName ?? '',
  from: entry.from ?? '',
  to: entry.to ?? null,
  graduation: entry.graduation ?? '',
  courseName: entry.courseName ?? '',
})

/**
 * Flattens the three backend-side education collections into a single
 * unified list for the Bildungsweg list view.
 */
export const collectEducationEntries = (
  account: AccountStoreData,
): EducationEntry[] => {
  const entries: EducationEntry[] = []
  for (const s of account.schoolEducations ?? []) {
    entries.push(schoolEducationToEntry(s))
  }
  for (const a of account.apprenticeships ?? []) {
    entries.push(apprenticeshipToEntry(a))
  }
  for (const s of account.studies ?? []) {
    entries.push(studyToEntry(s))
  }
  return entries
}

/**
 * Sorts education entries by their end ("to") date, most recent first.
 * Entries without an end date are treated as ongoing and sorted to the top.
 * Falls back to the start ("from") date when end dates are equal.
 */
export const sortEducationEntriesByLatestTo = (
  entries: EducationEntry[],
): EducationEntry[] =>
  [...entries].sort((a, b) => {
    if (a.to === b.to) return (b.from ?? '').localeCompare(a.from ?? '')
    if (!a.to) return -1
    if (!b.to) return 1
    return b.to.localeCompare(a.to)
  })

/**
 * Finds an education entry by UUID across all three collections.
 */
export const findEducationEntry = (
  account: AccountStoreData,
  uuid: string,
): EducationEntry | null => {
  const school = (account.schoolEducations ?? []).find((s) => s.uuid === uuid)
  if (school) return schoolEducationToEntry(school)

  const apprenticeship = (account.apprenticeships ?? []).find(
    (a) => a.uuid === uuid,
  )
  if (apprenticeship) return apprenticeshipToEntry(apprenticeship)

  const study = (account.studies ?? []).find((s) => s.uuid === uuid)
  if (study) return studyToEntry(study)

  return null
}

export type EducationFormState = {
  placeName: string
  type: EducationType | ''
  from: string
  to: string
  graduation: string
  courseName: string
}

export const loadFormFromEntry = (
  form: EducationFormState,
  entry: EducationEntry,
) => {
  form.placeName = entry.placeName
  form.type = entry.type
  form.from = sliceDateForInput(entry.from)
  form.to = sliceDateForInput(entry.to)
  form.graduation = entry.graduation
  form.courseName = entry.courseName ?? ''
}

type BuildArgs = {
  account: AccountStoreData
  previousType: EducationType | null
  previousUuid: string | null
  nextType: EducationType
  nextUuid: string
  placeName: string
  from: string
  to: string
  graduation: string
  courseName: string
}

/**
 * Builds the PATCH body for /account that:
 *   1. Inserts/updates the entry under its (new) type collection
 *   2. If the user changed the type during edit, removes the entry from the
 *      previous type's collection
 *
 * Only the collections that need to change are included in the payload.
 */
export const buildPatchPayloadForType = ({
  account,
  previousType,
  previousUuid,
  nextType,
  nextUuid,
  placeName,
  from,
  to,
  graduation,
  courseName,
}: BuildArgs): Partial<AccountStoreData> => {
  const patch: Partial<AccountStoreData> = {}
  const toValue = to.trim() ? to : null

  const schoolEducationsWithoutPrev = (account.schoolEducations ?? []).filter(
    (s) =>
      !(
        previousType === CareerPathType.SCHOOL_EDUCATION &&
        s.uuid === previousUuid
      ),
  )
  const studiesWithoutPrev = (account.studies ?? []).filter(
    (s) =>
      !(previousType === CareerPathType.STUDIES && s.uuid === previousUuid),
  )
  const apprenticeshipsWithoutPrev = (account.apprenticeships ?? []).filter(
    (a) =>
      !(
        previousType === CareerPathType.APPRENTICESHIP &&
        a.uuid === previousUuid
      ),
  )

  if (nextType === CareerPathType.SCHOOL_EDUCATION) {
    const next = {
      uuid: nextUuid,
      from,
      to: toValue,
      schoolName: placeName,
      graduation,
    }
    const existing = schoolEducationsWithoutPrev
    const replaced = existing.some((s) => s.uuid === nextUuid)
      ? existing.map((s) => (s.uuid === nextUuid ? next : s))
      : [...existing, next]
    patch.schoolEducations = replaced
  } else if (nextType === CareerPathType.APPRENTICESHIP) {
    const next = {
      uuid: nextUuid,
      from,
      to: toValue,
      companyName: placeName,
      graduation,
    }
    const existing = apprenticeshipsWithoutPrev
    const replaced = existing.some((a) => a.uuid === nextUuid)
      ? existing.map((a) => (a.uuid === nextUuid ? next : a))
      : [...existing, next]
    patch.apprenticeships = replaced
  } else {
    const next = {
      uuid: nextUuid,
      from,
      to: toValue,
      universityName: placeName,
      courseName,
      graduation,
    }
    const existing = studiesWithoutPrev
    const replaced = existing.some((s) => s.uuid === nextUuid)
      ? existing.map((s) => (s.uuid === nextUuid ? next : s))
      : [...existing, next]
    patch.studies = replaced
  }

  if (previousType && previousType !== nextType) {
    if (
      previousType === CareerPathType.SCHOOL_EDUCATION &&
      nextType !== CareerPathType.SCHOOL_EDUCATION
    ) {
      patch.schoolEducations = schoolEducationsWithoutPrev
    }
    if (
      previousType === CareerPathType.APPRENTICESHIP &&
      nextType !== CareerPathType.APPRENTICESHIP
    ) {
      patch.apprenticeships = apprenticeshipsWithoutPrev
    }
    if (
      previousType === CareerPathType.STUDIES &&
      nextType !== CareerPathType.STUDIES
    ) {
      patch.studies = studiesWithoutPrev
    }
  }

  return patch
}

/**
 * Builds the PATCH body to remove a single education entry. Mirrors the
 * collection rules in buildPatchPayloadForType.
 */
export const buildDeletePayload = (
  account: AccountStoreData,
  type: EducationType,
  uuid: string,
): Partial<AccountStoreData> => {
  if (type === CareerPathType.SCHOOL_EDUCATION) {
    return {
      schoolEducations: (account.schoolEducations ?? []).filter(
        (s) => s.uuid !== uuid,
      ),
    }
  }
  if (type === CareerPathType.APPRENTICESHIP) {
    return {
      apprenticeships: (account.apprenticeships ?? []).filter(
        (a) => a.uuid !== uuid,
      ),
    }
  }
  return {
    studies: (account.studies ?? []).filter((s) => s.uuid !== uuid),
  }
}
